<template>
  <div
    v-if="!isLoading && Object.keys(identityProvider.login_url).length === 0"
    class="tw-absolute tw-w-full tw-z-10 tw-opacity-[0.3] tw-flex tw-h-full tw-flex-col tw-bg-white"
  >
    <div :variant="'secondary'" class="tw-absolute tw-left-[40%] tw-top-[25%]">
      <cl-spinner
        :loading-text="$t('loading')"
        :size="'large'"
        class="tw-mr-2"
      />
    </div>
  </div>
  <div class="tw-mb-6">
    {{ t("saml-sso-overview") }}
  </div>
  <t-saml-toggle-confirm-index
    :visible="toggleConfirmModalVisible"
    :status="true"
    @on-close="closeToggleConfirmModal"
    @on-enable="enabledSave"
    @on-save="onSave"
  ></t-saml-toggle-confirm-index>
  <t-disable-saml-index
    :visible="toggleDisableModalVisible"
    :status="true"
    @on-close="closeToggleConfirmModal"
    @on-confirm="disableSave"
  ></t-disable-saml-index>
  <section>
    <div v-if="!isLoading" :variant="'secondary'" disabled>
      <cl-spinner :loading-text="$t('loading')" :size="'medium'" class="mr-2" />
    </div>
    <div v-else class="tw-flex tw-flex-row">
      <authorisation-wrapper
        :resource="ADMINISTRATORS"
        :action="WRITE"
        v-slot="{ isAllowed: isWriteAllowed }"
      >
        <cl-toggle
          id="saml-sso-toggle"
          name="saml-sso-toggle"
          class="tw-mr-3 tw-mb-2 tw-pl-6"
          :data-test-id="`${tier}-saml-sso-toggle`"
          :value="form.enabled"
          @update:value="handleSamlToggleChange"
          :readonly="
            !isWriteAllowed ||
            (authUserRole === 'admin' &&
              tier === TIER.msps &&
              authUserLevel === 'msp') ||
            (authUserRole === 'admin' &&
              tier === TIER.customer &&
              authUserLevel === 'customer')
          "
        />
      </authorisation-wrapper>
      <div class="tw-flex tw-w-full tw-justify-between">
        <div>
          <span>
            {{ t("saml-sso") }}
          </span>
        </div>
        <div>
          <a
            :data-test-id="`${tier}-settings-saml-learn-more`"
            target="_blank"
            href="https://support.titanhq.com/en/index-en.html?contextId=saml-learn-more"
            class="tw-flex tw-ml-auto tw-cursor-pointer hover:tw-no-underline tw-underline"
          >
            {{ t("learn-more") }}
            <cb-icon-external-link class="tw-mt-1 tw-ml-1" :width="16"
          /></a>
        </div>
      </div>
    </div>
  </section>

  <section v-if="form.enabled" class="tw-pl-32 tw-pr-32 tw-pb-32">
    <section class="tw-mt-8 tw-mb-6">
      <div class="tw-mb-8">
        <cl-heading :size="'h5'" class="tw-flex tw-items-center tw-gap-2">
          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <cl-input-checkbox
              id="send_customer_statistics"
              :checked="form.default_login_enabled"
              :readonly="
                !isWriteAllowed ||
                (authUserRole === 'admin' &&
                  tier === TIER.msps &&
                  authUserLevel === 'msp') ||
                (authUserRole === 'admin' &&
                  tier === TIER.customer &&
                  authUserLevel === 'customer')
              "
              :indeterminate="false"
              name="send_customer_statistics"
              @change="
                form.default_login_enabled = (
                  $event.target as HTMLInputElement
                ).checked
              "
            />
          </authorisation-wrapper>
          {{ t("allow-username-password-login") }}
        </cl-heading>
        <div class="tw-flex tw-items-center tw-gap-2 tw-pl-8">
          <cb-icon-insight class="tw-text-branding-blue-600" />
          <label>{{ t("testing-config-only") }}</label>
        </div>
      </div>

      <cl-heading :size="'h4'">
        {{ t("saml-sso-configure-your-ip-text-block") }}
      </cl-heading>
      <div class="tw-mb-6 tw-mt-6">
        {{ t("saml-sso-configure-your-ip-hint") }}
      </div>
      <div class="tw-pl-8 tw-pr-8">
        <div class="tw-grid tw-grid-cols-1 tw-gap-5 max-md:tw-grid-cols-1">
          <div class="tw-mb-2">
            <cl-form-group class="tw-relative">
              <cl-form-label
                :label-for="'identifier'"
                class="hover:tw-cursor-pointer tw-mr-4"
              >
                {{ t("saml-sso-identifier-label") }}
              </cl-form-label>
              <cl-form-input
                :id="'identifier'"
                v-model:value="form.service_provider.entity_id"
                :name="'identifier'"
                :type="'text'"
                disabled
                :data-test-id="`${tier}-config-identifier`"
              />
              <t-copy-button
                class="tw-bg-[#e7e8e9] tw-m-7 tw-absolute tw-right-0 tw-bottom-0"
                :value="form.service_provider.entity_id"
                :label="t('copy')"
                test-id="service_provider-entity_id-copy-btn"
              />
              <span class="tw-text-sm">
                {{ t("saml-sso-identifier-hint") }}
              </span>
            </cl-form-group>
          </div>
          <div class="tw-mb-2">
            <cl-form-group class="tw-relative">
              <cl-form-label
                :label-for="'reply'"
                class="hover:tw-cursor-pointer tw-mr-4"
              >
                {{ t("saml-sso-reply-url-label") }}
              </cl-form-label>
              <div class="tw-flex">
                <cl-form-input
                  :id="'reply'"
                  v-model:value="form.service_provider.consumer_url"
                  :name="'reply'"
                  :type="'text'"
                  disabled
                  :data-test-id="`${tier}-config-reply`"
                />
                <t-copy-button
                  class="tw-bg-[#e7e8e9] tw-m-7 tw-absolute tw-right-0 tw-bottom-0"
                  :value="form.service_provider.consumer_url"
                  :label="t('copy')"
                  test-id="service_provider-consumer_url-copy-btn"
                />
              </div>
              <span class="tw-text-sm">
                {{ t("saml-sso-reply-url-hint") }}
              </span>
            </cl-form-group>
          </div>
        </div>
      </div>
    </section>

    <section class="tw-mt-6">
      <cl-heading :size="'h4'">
        {{ t("saml-sso-authentication") }}
      </cl-heading>
      <div class="tw-mb-6 tw-mt-6">
        {{ t("saml-sso-setup-authentication") }}
      </div>
      <div class="tw-mb-6 tw-pl-8 tw-pr-8">
        <section class="tw-mb-8">
          <cl-form-group class="tw-relative">
            <cl-form-label
              :label-for="'login_url'"
              class="hover:tw-cursor-pointer tw-mr-4"
            >
              {{ t("saml-sso-login-url-label") }}
            </cl-form-label>
            <div>
              <authorisation-wrapper
                :resource="ADMINISTRATORS"
                :action="WRITE"
                v-slot="{ isAllowed: isWriteAllowed }"
              >
                <cl-form-input
                  :id="'login_url'"
                  v-model:value="form.identity_provider.login_url"
                  :name="'login_url'"
                  :type="'text'"
                  :state="
                    !v$.identity_provider.login_url.$dirty
                      ? undefined
                      : !v$.identity_provider.login_url.$error
                  "
                  :placeholder="'https://login.identityprovider.com/23578cec-f121-4770-969b-6b8d3a1044e8/saml2'"
                  :data-test-id="`${tier}-saml-config-login_url`"
                  class="tw-mb-2"
                  :readonly="
                    !isWriteAllowed ||
                    (authUserRole === 'admin' &&
                      tier === TIER.msps &&
                      authUserLevel === 'msp') ||
                    (authUserRole === 'admin' &&
                      tier === TIER.customer &&
                      authUserLevel === 'customer')
                  "
                />
              </authorisation-wrapper>
              <span
                v-if="v$.identity_provider.login_url.$error"
                class="tw-text-system-error-500"
                >{{ t("allowed-url-validation") }}</span
              >
            </div>
            <span class="tw-text-sm">
              {{ t("saml-sso-login-url-hint") }}
            </span>
          </cl-form-group>
        </section>
        <section class="tw-mb-8">
          <cl-form-group class="tw-relative">
            <cl-form-label
              :label-for="'entity_id'"
              class="hover:tw-cursor-pointer tw-mr-4"
            >
              {{ t("saml-sso-ip-issuer-label") }}
            </cl-form-label>
            <div>
              <authorisation-wrapper
                :resource="ADMINISTRATORS"
                :action="WRITE"
                v-slot="{ isAllowed: isWriteAllowed }"
              >
                <cl-form-input
                  :id="'entity_id'"
                  v-model:value="form.identity_provider.entity_id"
                  :name="'entity_id'"
                  :type="'text'"
                  :placeholder="'https://sts.identityprovider.com/23578cec-f121-4770-969b-6b8d3a1044e8/'"
                  :data-test-id="`${tier}-saml-config-entity_id`"
                  class="tw-mb-2"
                  :state="
                    !v$.identity_provider.entity_id.$dirty
                      ? undefined
                      : !v$.identity_provider.entity_id.$error
                  "
                  :readonly="
                    !isWriteAllowed ||
                    (authUserRole === 'admin' &&
                      tier === TIER.msps &&
                      authUserLevel === 'msp') ||
                    (authUserRole === 'admin' &&
                      tier === TIER.customer &&
                      authUserLevel === 'customer')
                  "
                />
              </authorisation-wrapper>
              <span
                v-if="v$.identity_provider.entity_id.$error"
                class="tw-text-system-error-500"
                >{{ t("allowed-url-validation") }}</span
              >
            </div>
            <span class="tw-text-sm">
              {{ t("saml-sso-ip-issuer-hint") }}
            </span>
          </cl-form-group>
        </section>
        <section class="tw-mb-8">
          <cl-form-group class="tw-relative">
            <cl-form-label
              :label-for="'logout_url'"
              class="hover:tw-cursor-pointer tw-mr-4"
            >
              {{ t("saml-sso-logout-url-label") }}
            </cl-form-label>
            <div>
              <authorisation-wrapper
                :resource="ADMINISTRATORS"
                :action="WRITE"
                v-slot="{ isAllowed: isWriteAllowed }"
              >
                <cl-form-input
                  :id="'logout_url'"
                  v-model:value="form.identity_provider.logout_url"
                  :name="'logout_url'"
                  :type="'text'"
                  :data-test-id="`${tier}-saml-config-logout_url`"
                  :placeholder="'https://login.identityprovider.com/23578cec-f121-4770-969b-6b8d3a1044e8/saml2'"
                  class="tw-mb-2"
                  :state="
                    !v$.identity_provider.logout_url.$dirty
                      ? undefined
                      : !v$.identity_provider.logout_url.$error
                  "
                  :readonly="
                    !isWriteAllowed ||
                    (authUserRole === 'admin' &&
                      tier === TIER.msps &&
                      authUserLevel === 'msp') ||
                    (authUserRole === 'admin' &&
                      tier === TIER.customer &&
                      authUserLevel === 'customer')
                  "
                />
              </authorisation-wrapper>
              <span
                v-if="v$.identity_provider.logout_url.$error"
                class="tw-text-system-error-500"
                >{{ t("allowed-url-validation") }}</span
              >
            </div>
            <span class="tw-text-sm">
              {{ t("saml-sso-logout-url-hint") }}
            </span>
          </cl-form-group>
        </section>

        <cl-form-group>
          <cl-form-label
            :label-for="'certificate'"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ t("saml-sso-public-certifcate") }}
          </cl-form-label>

          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <cl-form-textarea
              :id="'certificate'"
              v-model:value="form.identity_provider.certificate"
              :name="'certificate'"
              :type="'text'"
              :placeholder="t('saml-sso-public-certificate')"
              :state="
                !v$.identity_provider.certificate.$dirty
                  ? undefined
                  : !v$.identity_provider.certificate.$error
              "
              :data-test-id="`${tier}-saml-config-certificate`"
              class="tw-mb-2 tw-h-60"
              :readonly="
                !isWriteAllowed ||
                (authUserRole === 'admin' &&
                  tier === TIER.msps &&
                  authUserLevel === 'msp') ||
                (authUserRole === 'admin' &&
                  tier === TIER.customer &&
                  authUserLevel === 'customer')
              "
            >
            </cl-form-textarea>
          </authorisation-wrapper>
          <span
            v-if="v$.identity_provider.certificate.$error"
            class="tw-text-system-error-500"
            >{{ t("2fa-code-validation") }}</span
          >
        </cl-form-group>
      </div>
    </section>
    <section class="tw-mt-12">
      <cl-heading :size="'h4'">
        {{ t("saml-sso-attributes-text-block") }}
      </cl-heading>

      <div class="tw-pl-8 tw-pr-8">
        <cl-form-group class="tw-relative">
          <cl-form-label
            :label-for="'user_id'"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ t("saml-sso-attributes-label") }}
          </cl-form-label>
          <div>
            <authorisation-wrapper
              :resource="ADMINISTRATORS"
              :action="WRITE"
              v-slot="{ isAllowed: isWriteAllowed }"
            >
              <cl-form-input
                :id="'attr_claim'"
                v-model:value="form.identity_provider.attributes.attr_claim"
                :name="'attr_claim'"
                :placeholder="t('saml-sso-attributes-placeholder')"
                :type="'text'"
                :state="
                  !v$.identity_provider.attributes.attr_claim.$dirty
                    ? undefined
                    : !v$.identity_provider.attributes.attr_claim.$error
                "
                :data-test-id="`${tier}-saml-config-attr_claim`"
                class="tw-mb-2"
                :readonly="
                  !isWriteAllowed ||
                  (authUserRole === 'admin' &&
                    tier === TIER.msps &&
                    authUserLevel === 'msp') ||
                  (authUserRole === 'admin' &&
                    tier === TIER.customer &&
                    authUserLevel === 'customer')
                "
              />
            </authorisation-wrapper>
            <span
              v-if="v$.identity_provider.attributes.attr_claim.$error"
              class="tw-text-system-error-500"
              >{{ t("2fa-code-validation") }}</span
            >
          </div>
          <span class="tw-text-sm">
            {{ t("saml-sso-attributes-claim") }}
          </span>
        </cl-form-group>

        <authorisation-wrapper
          :resource="ADMINISTRATORS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <div v-if="!isSSOConfiged" class="tw-flex tw-justify-end tw-gap-2">
            <cl-button v-if="savingSaml" :variant="'secondary'" disabled>
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="mr-2"
              />
              <span>Saving...</span>
            </cl-button>
            <cl-button
              v-else
              type="submit"
              variant="secondary"
              :disabled="v$.identity_provider.$invalid"
              :readonly="
                !isWriteAllowed ||
                (authUserRole === 'admin' &&
                  tier === TIER.msps &&
                  authUserLevel === 'msp') ||
                (authUserRole === 'admin' &&
                  tier === TIER.customer &&
                  authUserLevel === 'customer')
              "
              :data-test-id="`${tier}-saml-save-sso-settings`"
              @on-click="saveSso"
            >
              {{ t("save") }}
            </cl-button>
          </div>
          <div v-if="isSSOConfiged" class="tw-flex tw-justify-end tw-gap-2">
            <cl-button v-if="savingSaml" :variant="'secondary'" disabled>
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="mr-2"
              />
              <span>Updating...</span>
            </cl-button>
            <cl-button
              v-else
              type="submit"
              variant="secondary"
              :disabled="v$.identity_provider.$invalid"
              :readonly="
                !isWriteAllowed ||
                (authUserRole === 'admin' &&
                  tier === TIER.msps &&
                  authUserLevel === 'msp') ||
                (authUserRole === 'admin' &&
                  tier === TIER.customer &&
                  authUserLevel === 'customer')
              "
              :data-test-id="`${tier}-saml-save-sso-settings`"
              @on-click="saveSso"
            >
              {{ t("update") }}
            </cl-button>
          </div>
        </authorisation-wrapper>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import { reactive, toRefs, ref, watch, computed } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useI18n } from "vue-i18n";
import { required, url, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { IdentityProvider, SsoConfigured } from "../../.././store/types";
import TSamlToggleConfirmIndex from "@/modals/t-saml-toggle-confirm/t-saml-toggle-confirm.index.vue";
import TDisableSamlIndex from "@/modals/t-disable-saml/t-disable-saml.index.vue";
import {
  getApiClientUrl,
  getSamlConsumerUrl,
  getSamlEntityId,
  getSamlLogoutUrl,
} from "@/config/runtimeConfig";

import { ADMINISTRATORS, AuthorisationWrapper, WRITE } from "@/authorisation";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { useSsoStore } from "@/modules/Settings/store/sso.store";
import TCopyButton from "@/components/TCopyButton/TCopyButton.vue";
import { TIER } from "@/constants";

const toastStore = useToastStore();
const ssoStore = useSsoStore();

const authUser = computed(() => authStore.authUser);
const authUserRole = computed(() => authUser.value?.role);
const authUserLevel = computed(() => authUser.value?.level);

const { t } = useI18n();
const authStore = useAuthenticationStore();
const isSSOConfiged = ref(false);
const toggleDisableModalVisible = ref(false);
const toggleConfirmModalVisible = ref(false);
const noEmoji = helpers.withMessage(
  "Emojis are not allowed",
  (value: string) =>
    !/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u.test(value),
);

interface TSamlProps {
  uuid: string;
  tier: string;
  identityProvider: IdentityProvider;
  isEditable: SsoConfigured;
}
const props = defineProps<TSamlProps>();
const { tier, uuid, identityProvider, isEditable } = toRefs(props);
const isLoading = ref(false);

const checkingSamlAvailabilty = (isEnabled: boolean) => {
  return isEnabled;
};
const form = reactive({
  enabled: checkingSamlAvailabilty(isEditable.value.sso),
  default_login_enabled: identityProvider.value.default_login_enabled || false,
  service_provider: {
    entity_id: `${getSamlEntityId()}${uuid.value}`,
    consumer_url: `${getApiClientUrl()}/v1${getSamlConsumerUrl()}`,
    logout_url: getSamlLogoutUrl(),
  },
  identity_provider: {
    login_url: identityProvider.value.login_url || "",
    entity_id: identityProvider.value.entity_id || "",
    logout_url: identityProvider.value.logout_url || "",
    certificate: identityProvider.value.certificate || "",
    attributes: {
      attr_claim: identityProvider.value.attributes?.email || "",
    },
  },
});
watch(
  () => identityProvider.value,
  (newValue) => {
    form.identity_provider.login_url = newValue?.login_url || "";
    form.identity_provider.entity_id = newValue?.entity_id || "";
    form.identity_provider.logout_url = newValue?.logout_url || "";
    form.identity_provider.certificate =
      atob(newValue?.certificate || "") || "";
    form.identity_provider.attributes.attr_claim =
      newValue?.attributes?.email || "";
    form.default_login_enabled = newValue?.default_login_enabled || false;
  },
);

watch(
  () => isEditable.value,
  (val) => {
    isLoading.value = true;
    form.enabled = val.sso;
    if (val.configured) {
      isSSOConfiged.value = val.configured;
    }
  },
);
const rules = {
  identity_provider: {
    login_url: { required, url, noEmoji },
    entity_id: { required, url, noEmoji },
    logout_url: { required, url, noEmoji },
    certificate: { required, noEmoji },
    attributes: {
      attr_claim: { required, noEmoji },
    },
  },
};
const v$ = useVuelidate(rules, form, { $autoDirty: true });
const savingSaml = ref(false);
const saveSso = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }
  toggleConfirmModalVisible.value = true;
  form.enabled = toggleConfirmModalVisible.value;
};
const handleSamlToggleChange = async (newValue: boolean) => {
  form.enabled = newValue;

  if (!newValue) {
    if (await v$.value.$validate()) {
      toggleDisableModalVisible.value = true;
    }
  } else {
    toggleConfirmModalVisible.value = isEditable.value.configured;
  }
};
const enabledSave = async () => {
  isEditable.value.configured || isSSOConfiged.value
    ? enabledUpdateSave()
    : enabledCreateSave();
};
const onSave = async () => {
  isEditable.value.configured || isSSOConfiged.value
    ? onUpdateSave()
    : onCreateSave();
};
const disableSave = async () => {
  isEditable.value.configured || isSSOConfiged.value
    ? disableUpdateSave()
    : disableCreateSave();
};

const onUpdateSave = async () => {
  form.enabled = false;
  try {
    const res = await ssoStore.updateSamlSSO({
      uuid: uuid.value,
      data: form,
    });
    if (res.status === 200) {
      toastStore.displayToast({
        title: t("success"),
        message: t("sso-config-saved"),
        duration: 5000,
        variant: "success",
        testId: "settings-saml-update-toast-success",
      });
    }
  } finally {
    closeToggleConfirmModal();
  }
};

const onCreateSave = async () => {
  form.enabled = false;
  try {
    const res = await ssoStore.createSamlSSO({
      uuid: uuid.value,
      data: form,
    });
    if (res.status === 200) {
      toastStore.displayToast({
        title: t("success"),
        message: t("sso-config-saved"),
        duration: 5000,
        variant: "success",
        testId: "settings-saml-create-toast-success",
      });

      isSSOConfiged.value = true;
    }
  } finally {
    closeToggleConfirmModal();
  }
};
const enabledUpdateSave = async () => {
  form.enabled = true;
  savingSaml.value = true;
  isLoading.value = false;
  try {
    const res = await ssoStore.updateSamlSSO({
      uuid: uuid.value,
      data: form,
    });
    if (res.status === 200) {
      toastStore.displayToast({
        title: t("success"),
        message: t("sso-enabled"),
        duration: 5000,
        variant: "success",
        testId: "settings-saml-enable-update-toast-success",
      });
    }
  } finally {
    closeToggleConfirmModal();
  }
};
const enabledCreateSave = async () => {
  form.enabled = true;
  savingSaml.value = true;
  isLoading.value = false;
  try {
    const res = await ssoStore.createSamlSSO({
      uuid: uuid.value,
      data: form,
    });
    if (res.status === 200) {
      toastStore.displayToast({
        title: t("success"),
        message: t("sso-enabled"),
        duration: 5000,
        variant: "success",
        testId: "settings-saml-enable-save-toast-success",
      });

      isSSOConfiged.value = true;
    }
  } finally {
    closeToggleConfirmModal();
  }
};
const disableUpdateSave = async () => {
  form.enabled = false;
  savingSaml.value = true;
  isLoading.value = false;
  try {
    const res = await ssoStore.updateSamlSSO({
      uuid: uuid.value,
      data: form,
    });
    if (res.status === 200) {
      toastStore.displayToast({
        title: t("success"),
        message: t("sso-disabled"),
        duration: 5000,
        variant: "success",
        testId: "settings-saml-disable-update-toast-success",
      });
    }
  } finally {
    closeToggleConfirmModal();
  }
};
const disableCreateSave = async () => {
  form.enabled = false;
  savingSaml.value = true;
  isLoading.value = false;
  try {
    const res = await ssoStore.createSamlSSO({
      uuid: uuid.value,
      data: form,
    });
    if (res.status === 200) {
      toastStore.displayToast({
        title: t("success"),
        message: t("sso-disabled"),
        duration: 5000,
        variant: "success",
        testId: "settings-saml-disable-create-toast-success",
      });
    }
  } finally {
    closeToggleConfirmModal();
  }
};
const closeToggleConfirmModal = async () => {
  toggleConfirmModalVisible.value = false;
  toggleDisableModalVisible.value = false;
  savingSaml.value = false;
  isLoading.value = true;
};
</script>
